import React, { createContext, useContext, useEffect, useState } from "react";
import {
  getProfilKeyCloak,
  getResourceAccess,
  getTokenKeycloak,
  getUserInfoKeyCloak,
  initKeycloak,
  loginKeycloak,
  updateToken,
} from "@core/keycloak/keycloak-config.ts";
import { AuthEntity } from "@core/services/AuthEntity.tsx";
import { useAppDispatch, useAppSelector } from "@core/store/hook.ts";
import {
  profileKeycloak,
  setProfileKeycloak,
  setUserInfoKeycloak,
  setUserRole,
} from "@core/slice/UsersSlice.ts";
import { getUserInfo } from "@core/actions/usersActions.ts";
import { auditLogService } from "@core/services/auditLog.service.ts";

interface AuthContextProps {
  authenticated: boolean;
  profil: Keycloak.KeycloakProfile | undefined;
  userInfo: Keycloak.KeycloakProfile | undefined;
  roles: string[];
  hasRole: (role: string) => boolean;
  loading: boolean;
}

const AuthContext = createContext<AuthContextProps | null>(null);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const dispatch = useAppDispatch();
  const profil = useAppSelector(profileKeycloak);
  // const userInformation = useAppSelector(userInfoKeycloak) // ici pour récupérer les informations sur l'utilisateur
  // const rolesUser = useAppSelector(userRole) // ici pour récupérer les roles de l'utilisateur
  const [authenticated, setAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [userInfo, setUserInfo] = useState<
    Keycloak.KeycloakProfile | undefined
  >(undefined);
  const [roles, setRoles] = useState<string[]>([]);

  useEffect(() => {
    const initializeAuth = async () => {
      try {
        const isLogged = await initKeycloak();
        if (isLogged) {
          const token = await getTokenKeycloak();
          localStorage.setItem("token", JSON.stringify(token));
          AuthEntity.setToken(token);

          setAuthenticated(true);
          const [profile, userInfo, resourceAccess] = await Promise.all([
            getProfilKeyCloak(),
            getUserInfoKeyCloak(),
            getResourceAccess(),
          ]);
          dispatch(setProfileKeycloak(profile));
          dispatch(setUserInfoKeycloak(userInfo));
          setUserInfo(userInfo);

          if (profile && profile.id) {
            dispatch(getUserInfo(profile.id));
          }
          let allRoles: string[] = [];
          if (resourceAccess) {
            Object.values(resourceAccess).forEach((access: any) => {
              if (access?.roles) {
                allRoles.push(...access.roles);
              }
            });
            setRoles(allRoles);
          }
          dispatch(setUserRole(allRoles));

          // Stocker les infos utilisateur pour le log de déconnexion
          const auditUserId = String(profile?.id ?? "unknown");
          const auditUsername =
            profile
              ? `${profile.firstName ?? ""} ${profile.lastName ?? ""}`.trim() ||
                (profile.username ?? "unknown")
              : "unknown";
          const auditRole = allRoles.join(",") || "unknown";
          localStorage.setItem(
            "audit_user",
            JSON.stringify({ userId: auditUserId, username: auditUsername, role: auditRole })
          );

          // Logger la connexion une seule fois par session (évite les doublons au refresh)
          if (!sessionStorage.getItem("_audit_logged")) {
            sessionStorage.setItem("_audit_logged", "1");
            auditLogService.create({
              action: "LOGIN",
              userId: auditUserId,
              username: auditUsername,
              role: auditRole,
              targetType: "SESSION",
              targetId: auditUserId,
              details: "Connexion réussie",
            }).catch(() => {/* ne pas bloquer l'auth si le log échoue */});
          }

          await updateToken();
        } else {
          loginKeycloak();
        }
      } catch (error) {
        console.error(
          "Erreur lors de l'initialisation de l'authentification :",
          error
        );
      } finally {
        setLoading(false);
      }
    };

    initializeAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const hasRole = (role: string): boolean => roles.includes(role);

  return (
    <AuthContext.Provider
      value={{ authenticated, profil, userInfo, roles, hasRole, loading }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextProps => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
